import React, { useState, useCallback } from "react";
import {
  Box,
  IconButton,
  useBreakpointValue,
  Stack,
  Heading,
  Text,
  ChakraProvider,
  VisuallyHidden,
  Image,
} from "@chakra-ui/react";
import { BiLeftArrowAlt, BiRightArrowAlt } from "react-icons/bi";
import Slider from "react-slick";

import viirsCarousel from "./data/img/viirs_worldview2.png";

// Carousel slide data - easily expandable for future slides
const carouselSlides = [
  {
    id: "slide-1",
    title: "Giglio Fire Group",
    text: "Image Source: NASA Worldview",
    image: viirsCarousel,
    alt: "Wide satellite image of Earth showing global cloud patterns and continents, with numerous red dots indicating active fire detections across South America, Africa, and Asia. Black triangular graphic elements appear in the foreground as part of the banner design.",
  },
  // Add more slides here as needed:
  // {
  //   id: "slide-2",
  //   title: "New Slide Title",
  //   text: "Slide description",
  //   image: yourImage,
  //   alt: "Descriptive alt text for the image",
  // },
];

// Inner component that uses Chakra hooks (must be inside ChakraProvider)
function CarouselContent() {
  const [slider, setSlider] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Responsive values - these hooks now have access to ChakraProvider context
  const carouselHeight = useBreakpointValue({
    base: "300px",
    sm: "350px",
    md: "400px",
    lg: "450px",
  }) || "400px";

  const buttonSize = useBreakpointValue({
    base: "32px",
    md: "40px",
  }) || "40px";

  const headingSize = useBreakpointValue({
    base: "2xl",
    sm: "3xl",
    md: "4xl",
    lg: "5xl",
  }) || "4xl";

  const textSize = useBreakpointValue({
    base: "sm",
    sm: "md",
    lg: "lg",
  }) || "md";

  // Determine if navigation should be shown (only if more than one slide)
  const showNavigation = carouselSlides.length > 1;

  // Accessible navigation handlers
  const goToPrevious = useCallback(() => {
    slider?.slickPrev();
  }, [slider]);

  const goToNext = useCallback(() => {
    slider?.slickNext();
  }, [slider]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event) => {
      if (!showNavigation) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNext();
      }
    },
    [goToPrevious, goToNext, showNavigation]
  );

  // Settings for the slider with accessibility enhancements
  const settings = {
    dots: showNavigation,
    arrows: false,
    fade: false,
    infinite: showNavigation,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: (dots) => (
      <Box
        as="ul"
        position="absolute"
        bottom={{ base: "10px", md: "20px" }}
        width="100%"
        display="flex"
        justifyContent="center"
        padding="0"
        margin="0"
        listStyleType="none"
        role="tablist"
        aria-label="Carousel navigation dots"
      >
        {dots}
      </Box>
    ),
    customPaging: (index) => (
      <Box
        as="button"
        role="tab"
        aria-label={`Go to slide ${index + 1} of ${carouselSlides.length}`}
        aria-selected={currentSlide === index}
        tabIndex={currentSlide === index ? 0 : -1}
        width={{ base: "10px", md: "12px" }}
        height={{ base: "10px", md: "12px" }}
        borderRadius="full"
        bg={currentSlide === index ? "orange.400" : "whiteAlpha.600"}
        border="2px solid"
        borderColor={currentSlide === index ? "orange.400" : "whiteAlpha.800"}
        transition="all 0.2s"
        _hover={{
          bg: currentSlide === index ? "orange.500" : "whiteAlpha.800",
        }}
        _focus={{
          outline: "2px solid",
          outlineColor: "blue.400",
          outlineOffset: "2px",
        }}
      />
    ),
  };

  return (
    <>
      {/* CSS files for react-slick */}
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />

      <Box
        as="section"
        aria-roledescription="carousel"
        aria-label="Featured images carousel"
        position="relative"
        height={carouselHeight}
        width="full"
        overflow="hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Live region for announcing slide changes to screen readers */}
        <VisuallyHidden>
          <Box aria-live="polite" aria-atomic="true">
            {`Showing slide ${currentSlide + 1} of ${carouselSlides.length}: ${
              carouselSlides[currentSlide]?.title
            }`}
          </Box>
        </VisuallyHidden>

        {/* Previous Slide Button - only show if multiple slides */}
        {showNavigation && (
          <IconButton
            aria-label={`Go to previous slide (currently on slide ${
              currentSlide + 1
            } of ${carouselSlides.length})`}
            variant="ghost"
            position="absolute"
            left={{ base: "8px", sm: "16px", md: "40px" }}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            onClick={goToPrevious}
            bg="blackAlpha.500"
            color="white"
            borderRadius="full"
            size={{ base: "sm", md: "lg" }}
            _hover={{
              bg: "blackAlpha.700",
            }}
            _focus={{
              outline: "3px solid",
              outlineColor: "blue.400",
              outlineOffset: "2px",
              bg: "blackAlpha.700",
            }}
            _disabled={{
              opacity: 0.4,
              cursor: "not-allowed",
            }}
            isDisabled={!settings.infinite && currentSlide === 0}
          >
            <BiLeftArrowAlt size={buttonSize} aria-hidden="true" />
          </IconButton>
        )}

        {/* Next Slide Button - only show if multiple slides */}
        {showNavigation && (
          <IconButton
            aria-label={`Go to next slide (currently on slide ${
              currentSlide + 1
            } of ${carouselSlides.length})`}
            variant="ghost"
            position="absolute"
            right={{ base: "8px", sm: "16px", md: "40px" }}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            onClick={goToNext}
            bg="blackAlpha.500"
            color="white"
            borderRadius="full"
            size={{ base: "sm", md: "lg" }}
            _hover={{
              bg: "blackAlpha.700",
            }}
            _focus={{
              outline: "3px solid",
              outlineColor: "blue.400",
              outlineOffset: "2px",
              bg: "blackAlpha.700",
            }}
            _disabled={{
              opacity: 0.4,
              cursor: "not-allowed",
            }}
            isDisabled={
              !settings.infinite && currentSlide === carouselSlides.length - 1
            }
          >
            <BiRightArrowAlt size={buttonSize} aria-hidden="true" />
          </IconButton>
        )}

        {/* Slider */}
        <Slider {...settings} ref={(sliderRef) => setSlider(sliderRef)}>
          {carouselSlides.map((slide, index) => (
            <Box
              key={slide.id}
              role="tabpanel"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${carouselSlides.length}: ${
                slide.title
              }`}
              aria-hidden={currentSlide !== index}
              height={carouselHeight}
              position="relative"
            >
              {/* Background Image with proper alt text */}
              <Image
                src={slide.image}
                alt={slide.alt}
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                objectFit="cover"
                objectPosition="center"
              />

              {/* Content Overlay */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="blackAlpha.200"
              >
                <Stack
                  spacing={{ base: 3, md: 6 }}
                  maxW={{ base: "90%", sm: "80%", md: "2xl" }}
                  textAlign="center"
                  bg="rgba(50, 50, 50, 0.75)"
                  borderRadius="md"
                  p={{ base: 3, sm: 4, md: 6 }}
                  mx={{ base: 2, sm: 4 }}
                >
                  <Heading
                    as="h2"
                    fontSize={headingSize}
                    color="white"
                    textShadow="1px 1px 2px rgba(0,0,0,0.5)"
                  >
                    {slide.title}
                  </Heading>
                  <Text
                    fontSize={textSize}
                    color="gray.100"
                    textShadow="1px 1px 2px rgba(0,0,0,0.5)"
                  >
                    {slide.text}
                  </Text>
                </Stack>
              </Box>
            </Box>
          ))}
        </Slider>

        {/* Skip link for keyboard users */}
        <VisuallyHidden>
          <a href="#main-content" tabIndex={0}>
            Skip carousel
          </a>
        </VisuallyHidden>
      </Box>
    </>
  );
}

// Main export - wraps content in ChakraProvider if needed
export default function CaptionCarousel() {
  return (
    <ChakraProvider>
      <CarouselContent />
    </ChakraProvider>
  );
}